import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  Divider,
  Chip,
  Alert,
  SelectChangeEvent,
  Autocomplete
} from '@mui/material';
import Download from '@mui/icons-material/Download';
import { CountyData, CropData } from '../services/countyService';
import { webScrapingService } from '../services/webScrapingService';
import { MarketIntelligenceService, type MarketData } from '../services/marketIntelligenceService';
import useChartRegistration from '../components/charts/ChartRegistration';
import ChartComponent from '../components/charts/chart';

// Create unique IDs for each chart
const CHART_IDS = {
  marketPrice: 'market-price-chart',
  cropComparison: 'crop-comparison-chart',
  marketTrend: 'market-trend-chart',
  international: 'international-comparison-chart'
};

// Full list of Kenyan counties for the dropdown
const AVAILABLE_COUNTIES = [
  'Mombasa', 'Kwale', 'Kilifi', 'Tana River', 'Lamu', 'Taita-Taveta',
  'Garissa', 'Wajir', 'Mandera', 'Marsabit', 'Isiolo', 'Meru', 'Tharaka-Nithi',
  'Embu', 'Kitui', 'Machakos', 'Makueni', 'Nyandarua', 'Nyeri', 'Kirinyaga',
  "Murang'a", 'Kiambu', 'Turkana', 'West Pokot', 'Samburu', 'Trans Nzoia',
  'Uasin Gishu', 'Elgeyo-Marakwet', 'Nandi', 'Baringo', 'Laikipia', 'Nakuru',
  'Narok', 'Kajiado', 'Kericho', 'Bomet', 'Kakamega', 'Vihiga', 'Bungoma',
  'Busia', 'Siaya', 'Kisumu', 'Homa Bay', 'Migori', 'Kisii', 'Nyamira',
  'Nairobi'
];

const AVAILABLE_CROPS = [
  'Maize', 'Beans', 'Potatoes', 'Wheat', 'Rice', 'Sorghum', 'Millet',
  'Coffee', 'Tea', 'Sugarcane', 'Cotton', 'Vegetables', 'Bananas',
  'Avocado', 'Green grams', 'Sweet potatoes', 'Cassava'
];

const AgriDashboard = () => {
  const location = useLocation();
  const { isRegistered: chartJsReady, error: chartJsError } = useChartRegistration();
  
  // Chart instance refs to manage cleanup
  const chartRefs = useRef({
    marketPrice: null,
    cropComparison: null,
    marketTrend: null,
    international: null
  });
  
  // Check URL parameters for initial values
  const queryParams = new URLSearchParams(location.search);
  const countyFromUrl = queryParams.get('county');
  const cropFromUrl = queryParams.get('crop');

  const [countyQuery, setCountyQuery] = useState(countyFromUrl || 'Kiambu');
  const [cropQuery, setCropQuery] = useState(cropFromUrl || 'Maize');
  const [selectedCounty, setSelectedCounty] = useState<CountyData | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<CropData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableCounties] = useState(AVAILABLE_COUNTIES);
  const [availableCrops] = useState(AVAILABLE_CROPS);
  const [chartsError, setChartsError] = useState(false);
  const [dataSource, setDataSource] = useState<string>("Web Scraping Service");
  const [sourceCitations, setSourceCitations] = useState<string[]>([]);
  const [marketData, setMarketData] = useState<MarketData[] | null>(null);
  const [priceSource, setPriceSource] = useState<'official' | 'marketplace' | 'ncpb' | 'blended'>('official');

  const normalizeCropName = (name: string) => name.toLowerCase().trim();

  const isMatchingMarketplaceProduct = (productName: string, cropName: string) => {
    const normalizedProduct = normalizeCropName(productName);
    const normalizedCrop = normalizeCropName(cropName);

    if (normalizedProduct === normalizedCrop) return true;
    if (normalizedProduct.startsWith(`${normalizedCrop} `)) return true;
    if (normalizedProduct.endsWith(` ${normalizedCrop}`)) return true;
    if (normalizedProduct.includes(` ${normalizedCrop} `)) return true;

    const aliasMap: Record<string, string[]> = {
      maize: ['corn'],
      beans: ['dry beans', 'common beans'],
      potatoes: ['irish potatoes'],
      rice: [],
      wheat: []
    };

    const aliases = aliasMap[normalizedCrop] || [];
    if (aliases.some(alias => normalizedProduct === alias || normalizedProduct.startsWith(`${alias} `))) {
      return true;
    }

    return false;
  };

  // Show chart error if Chart.js failed to register
  useEffect(() => {
    if (chartJsError) {
      setChartsError(true);
      console.error("Chart.js registration error:", chartJsError);
    }
  }, [chartJsError]);
  
  // Cleanup chart instances on unmount
  useEffect(() => {
    return () => {
      // Destroy all chart instances on component unmount
      Object.values(chartRefs.current).forEach(chart => {
        if (chart) {
          // @ts-ignore - chart has a destroy method
          chart.destroy();
        }
      });
    };
  }, []);

  // Load initial data
  useEffect(() => {
    if (chartJsReady) {
      loadData();
    }
    
    // Add cleanup function
    return () => {
      setSelectedCounty(null);
      setSelectedCrop(null);
    };
  }, [chartJsReady]);

  // Load data when URL parameters change
  useEffect(() => {
    if (countyFromUrl && cropFromUrl) {
      setCountyQuery(countyFromUrl);
      setCropQuery(cropFromUrl);
      if (chartJsReady) {
        // Clear any existing chart instances before loading new data from URL
        Object.values(chartRefs.current).forEach(chart => {
          if (chart) {
            // @ts-ignore - chart has a destroy method
            chart.destroy();
          }
        });
        
        // Reset refs
        chartRefs.current = {
          marketPrice: null,
          cropComparison: null,
          marketTrend: null,
          international: null
        };
        
        loadData();
      }
    }
  }, [location.search, chartJsReady]);

  const loadData = async (overrideCounty?: string, overrideCrop?: string) => {
    setLoading(true);
    setError(null);
    setChartsError(false);
    setSourceCitations([]);
    setMarketData(null);

    const countyName = overrideCounty ?? countyQuery;
    const cropName = overrideCrop ?? cropQuery;

    try {
      console.log(`Loading data from backend agronomic API (with fallback): County=${countyName}, Crop=${cropName}`);
      setDataSource("Official agronomic datasets via FarmHub API");
      
      let countyData: CountyData | null = null;

      if (countyName) {
        countyData = await webScrapingService.getCountyByName(countyName);
        setSelectedCounty(countyData);
        const citations = countyData?.sourceCitations;
        if (citations) {
          setSourceCitations(prev => [...prev, ...citations]);
        }
      }

      if (cropName) {
        const cropData = await webScrapingService.getCropData(cropName);
        setSelectedCrop(cropData);
        if (cropData.sourceCitations) {
          setSourceCitations(prev => [...prev, ...cropData.sourceCitations!]);
        }
      }

      if (cropName && countyName) {
        const marketResults = await MarketIntelligenceService.getMarketData(
          cropName,
          countyName
        );

        const hasRealSupabaseData =
          marketResults && marketResults.some(entry => entry.source !== 'mock');

        if (!hasRealSupabaseData && countyData && Array.isArray(countyData.marketPrices)) {
          const syntheticFromCounty = countyData.marketPrices
            .filter(priceEntry =>
              isMatchingMarketplaceProduct(priceEntry.commodity, cropName)
            )
            .map((priceEntry, index): MarketData => ({
              id: `county-${countyName}-${cropName}-${index}`,
              product_name: cropName,
              category: 'Crops',
              location: countyName,
              price: priceEntry.price,
              unit: priceEntry.unit,
              quantity: 0,
              quality: 'standard',
              source: 'ncpb',
              date:
                (priceEntry as any).date ||
                new Date().toISOString(),
              trend: 'stable',
              demand_level: 'medium',
              supply_level: 'medium'
            }));

          if (syntheticFromCounty.length > 0) {
            setMarketData(syntheticFromCounty);
          } else {
            setMarketData(marketResults);
          }
        } else {
          setMarketData(marketResults);
        }
      }
    } catch (err) {
      console.error("Data loading error:", err);
      setError(`Failed to load agronomic data. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCountyChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setCountyQuery(newValue || '');
  };

  const handleCropChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setCropQuery(newValue || '');
  };

  const handlePriceSourceChange = (event: SelectChangeEvent<'official' | 'marketplace' | 'ncpb' | 'blended'>) => {
    const value = event.target.value as 'official' | 'marketplace' | 'ncpb' | 'blended';
    setPriceSource(value);
  };

  const runSearch = (overrideCounty?: string, overrideCrop?: string) => {
    Object.values(chartRefs.current).forEach(chart => {
      if (chart) {
        // @ts-ignore - chart has a destroy method
        chart.destroy();
      }
    });
    
    // Reset refs
    chartRefs.current = {
      marketPrice: null,
      cropComparison: null,
      marketTrend: null,
      international: null
    };
    
    loadData(overrideCounty, overrideCrop);
  };

  const handleSearch = () => {
    runSearch();
  };

  const handleExport = () => {
    if (!selectedCounty || !selectedCrop) return;

    const officialPrice = selectedCounty.marketPrices.find(
      p => p.commodity.toLowerCase() === selectedCrop.cropName.toLowerCase()
    );

    const marketplaceEntries = (marketData || []).filter(
      entry =>
        entry.source === 'marketplace' &&
        isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
    );

    const ncpbEntries = (marketData || []).filter(
      entry =>
        entry.source === 'ncpb' &&
        entry.location === selectedCounty.name &&
        isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
    );

    const marketplaceAverage =
      marketplaceEntries.length > 0
        ? marketplaceEntries.reduce((sum, entry) => sum + entry.price, 0) /
          marketplaceEntries.length
        : null;

    const ncpbAverage =
      ncpbEntries.length > 0
        ? ncpbEntries.reduce((sum, entry) => sum + entry.price, 0) / ncpbEntries.length
        : null;

    const effectivePrice =
      priceSource === 'official'
        ? officialPrice?.price ?? marketplaceAverage
        : priceSource === 'marketplace'
        ? marketplaceAverage ?? officialPrice?.price
        : priceSource === 'ncpb'
        ? ncpbAverage
        : officialPrice && marketplaceAverage
        ? (officialPrice.price + marketplaceAverage) / 2
        : officialPrice?.price ?? marketplaceAverage;

    const effectiveUnit =
      priceSource === 'ncpb'
        ? ncpbEntries[0]?.unit ?? ''
        : officialPrice?.unit || (marketplaceEntries[0]?.unit ?? '');

    const csvContent = [
      ['Report', 'Agricultural Planning Dashboard'],
      ['Date', new Date().toLocaleDateString()],
      ['County', selectedCounty.name],
      ['Crop', selectedCrop.cropName],
      [],
      ['Market Prices'],
      ['Commodity', 'Price', 'Unit', 'Date'],
      ...selectedCounty.marketPrices.map(p => [p.commodity, p.price, p.unit, p.date]),
      [],
      ['Crop Performance'],
      ['County', 'Score'],
      ...Object.entries(selectedCrop.countyData).map(([c, s]) => [c, s]),
      [],
      ['Price Analysis'],
      ['Price Source Preference', priceSource],
      [
        'Official Price (if available)',
        officialPrice ? `${officialPrice.price} per ${officialPrice.unit} (as of ${officialPrice.date})` : 'N/A'
      ],
      [
        'Marketplace Average Price',
        marketplaceAverage && marketplaceEntries.length > 0
          ? `${marketplaceAverage.toFixed(2)} per ${marketplaceEntries[0].unit} (${marketplaceEntries.length} records)`
          : 'N/A'
      ],
      [
        'NCPB Reference Price',
        ncpbAverage && ncpbEntries.length > 0
          ? `${ncpbAverage.toFixed(2)} per ${ncpbEntries[0].unit} (${ncpbEntries.length} records)`
          : 'N/A'
      ],
      [
        'Effective Price Used',
        effectivePrice
          ? `${effectivePrice.toFixed(2)}${effectiveUnit ? ` per ${effectiveUnit}` : ''}`
          : 'N/A'
      ]
    ].map(e => e.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedCounty.name}_${selectedCrop.cropName}_report.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Chart generation functions with error handling
  const renderMarketPriceChart = () => {
    if (!selectedCounty?.marketPrices || selectedCounty.marketPrices.length === 0) {
      return <Alert severity="info">No market price data available</Alert>;
    }

    const data = {
      labels: selectedCounty.marketPrices.map(price => price.commodity),
      datasets: [
        {
          label: 'Market Prices (KES)',
          data: selectedCounty.marketPrices.map(price => price.price),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    return (
      <ChartComponent
        type="bar"
        data={data}
        id={`market-price-${selectedCounty.name}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: `Market Prices in ${selectedCounty.name}`
            }
          }
        }}
      />
    );
  };

  const renderCropComparisonChart = () => {
    if (!selectedCrop?.countyData) {
      return <Alert severity="info">No crop comparison data available</Alert>;
    }

    const data = {
      labels: Object.keys(selectedCrop.countyData),
      datasets: [
        {
          label: `${selectedCrop.cropName} Performance Score`,
          data: Object.values(selectedCrop.countyData),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    return (
      <ChartComponent
        type="bar"
        data={data}
        id={`crop-comparison-${selectedCrop.cropName}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: `${selectedCrop.cropName} Performance by County`
            }
          }
        }}
      />
    );
  };

  const renderMarketTrendChart = () => {
    if (!selectedCrop?.marketTrend || selectedCrop.marketTrend.length === 0) {
      return <Alert severity="info">No market trend data available</Alert>;
    }

    const data = {
      labels: selectedCrop.marketTrend.map(trend => trend.month),
      datasets: [
        {
          label: `${selectedCrop.cropName} Price Trend (KES)`,
          data: selectedCrop.marketTrend.map(trend => trend.price),
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1
        }
      ]
    };

    return (
      <ChartComponent
        type="line"
        data={data}
        id={`market-trend-${selectedCrop.cropName}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: `${selectedCrop.cropName} Price Trends`
            }
          }
        }}
      />
    );
  };

  const renderInternationalComparisonChart = () => {
    if (!selectedCrop?.internationalComparison) {
      return <Alert severity="info">No international comparison data available</Alert>;
    }

    const data = {
      labels: Object.keys(selectedCrop.internationalComparison),
      datasets: [
        {
          label: 'Performance Score',
          data: Object.values(selectedCrop.internationalComparison),
          backgroundColor: [
            'rgba(255, 99, 132, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 206, 86, 0.5)',
            'rgba(75, 192, 192, 0.5)',
            'rgba(153, 102, 255, 0.5)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    return (
      <ChartComponent
        type="pie"
        data={data}
        id={`international-comparison-${selectedCrop.cropName}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: 'International Comparison'
            }
          }
        }}
      />
    );
  };

  const generateSuitabilityAnalysis = () => {
    if (!selectedCounty || !selectedCrop) return null;

    try {
      const isMainCrop = selectedCounty.mainCrops.includes(selectedCrop.cropName);
      const countyYieldScore = selectedCrop.countyData[selectedCounty.name];
      const comparedToNational = countyYieldScore > selectedCrop.nationalAverage ? 'Above' : 'Below';

      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Suitability Analysis: {selectedCrop.cropName} in {selectedCounty.name}
            </Typography>
            
            {isMainCrop ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {selectedCrop.cropName} is a main crop in {selectedCounty.name} County
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {selectedCrop.cropName} is not listed as a main crop in {selectedCounty.name} County
              </Alert>
            )}

            {countyYieldScore ? (
              <Typography variant="body1" gutterBottom>
                Yield Score: {countyYieldScore}/100 ({comparedToNational} national average of {selectedCrop.nationalAverage})
              </Typography>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No yield data available for {selectedCrop.cropName} in {selectedCounty.name} County
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Climate Compatibility
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  <strong>County Temperature:</strong> {selectedCounty.climate.temperature}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Crop Temperature Needs:</strong> {selectedCrop.growingConditions.temperature}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  <strong>County Rainfall:</strong> {selectedCounty.climate.rainfall}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Crop Water Needs:</strong> {selectedCrop.growingConditions.rainfall}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    } catch (error) {
      console.error("Error generating suitability analysis:", error);
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Suitability Analysis
            </Typography>
            <Alert severity="error">
              Failed to generate suitability analysis. Please try again.
            </Alert>
          </CardContent>
        </Card>
      );
    }
  };

  const generateMarketAnalysis = () => {
    if (!selectedCounty || !selectedCrop) return null;

    try {
      const officialPrice = selectedCounty.marketPrices.find(
        p => p.commodity.toLowerCase() === selectedCrop.cropName.toLowerCase()
      );

      const realEntries = (marketData || []).filter(entry =>
        (entry.source === 'marketplace' ||
          entry.source === 'external' ||
          entry.source === 'government' ||
          entry.source === 'news' ||
          entry.source === 'ncpb') &&
        entry.location === selectedCounty.name &&
        isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const mockEntries = (marketData || []).filter(
        entry =>
          entry.source === 'mock' &&
          isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const realAverage =
        realEntries.length > 0
          ? realEntries.reduce((sum, entry) => sum + entry.price, 0) /
            realEntries.length
          : null;

      const mockAverage =
        mockEntries.length > 0
          ? mockEntries.reduce((sum, entry) => sum + entry.price, 0) /
            mockEntries.length
          : null;

      const ncpbEntries = (marketData || []).filter(
        entry =>
          entry.source === 'ncpb' &&
          entry.location === selectedCounty.name &&
          isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const ncpbAverage =
        ncpbEntries.length > 0
          ? ncpbEntries.reduce((sum, entry) => sum + entry.price, 0) / ncpbEntries.length
          : null;

      const effectivePrice =
        priceSource === 'official'
          ? officialPrice?.price ?? realAverage ?? mockAverage
          : priceSource === 'marketplace'
          ? realAverage ?? officialPrice?.price ?? mockAverage
          : priceSource === 'ncpb'
          ? ncpbAverage
          : officialPrice && realAverage
          ? (officialPrice.price + realAverage) / 2
          : officialPrice?.price ?? realAverage ?? mockAverage;

      const effectiveUnit =
        priceSource === 'ncpb'
          ? ncpbEntries[0]?.unit ||
            officialPrice?.unit ||
            realEntries[0]?.unit ||
            mockEntries[0]?.unit ||
            ''
          : officialPrice?.unit ||
            realEntries[0]?.unit ||
            mockEntries[0]?.unit ||
            '';

      const entriesForSelectedCropAndCounty = (marketData || []).filter(
        entry =>
          entry.location === selectedCounty.name &&
          isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const sourceBreakdown = entriesForSelectedCropAndCounty.reduce(
        (acc, entry) => {
          const key = entry.source;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {} as Record<MarketData['source'], number | undefined>
      );

      const totalWeight =
        (officialPrice ? 0.5 : 0) +
        (realEntries.length > 0 ? 0.3 : 0) +
        (mockEntries.length > 0 ? 0.2 : 0);

      const adoptionPercentage =
        totalWeight > 0
          ? Math.round(
              ((officialPrice ? 0.5 : 0) + (realEntries.length > 0 ? 0.3 : 0)) /
                totalWeight *
                100
            )
          : 0;

      const isSyntheticNcpb = (marketData || []).some(entry => entry.id.startsWith('county-'));

      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Market Analysis
            </Typography>
            
            {effectivePrice ? (
              <>
                <Typography variant="body1" gutterBottom>
                  Current price in {selectedCounty.name}: {effectivePrice.toFixed(2)} KES
                  {effectiveUnit && ` per ${effectiveUnit}`}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Source preference:{' '}
                  {priceSource === 'official'
                    ? 'Official statistics (with marketplace fallback)'
                    : priceSource === 'marketplace'
                    ? 'Marketplace trades (with official fallback)'
                    : priceSource === 'ncpb'
                    ? 'NCPB reference'
                    : 'Blended view (average of official and marketplace)'}
                </Typography>
                {officialPrice && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Official reference: {officialPrice.price} KES per {officialPrice.unit} (as of {officialPrice.date})
                  </Typography>
                )}
                {realAverage && realEntries.length > 0 && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Market/news average ({realEntries.length} records):{' '}
                    {realAverage.toFixed(2)} KES per {realEntries[0].unit}
                  </Typography>
                )}
                {mockAverage && mockEntries.length > 0 && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Mock market benchmark ({mockEntries.length} records):{' '}
                    {mockAverage.toFixed(2)} KES per {mockEntries[0].unit}
                  </Typography>
                )}

                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Data adoption: {adoptionPercentage}% real-world data (official +
                  marketplace/news), {100 - adoptionPercentage}% modelled/mock benchmarks.
                </Typography>
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No price data available for {selectedCrop.cropName} in {selectedCounty.name} County
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Data snapshot for {selectedCrop.cropName} in {selectedCounty.name}:
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div">
                Total entries: {entriesForSelectedCropAndCounty.length} | 
                Marketplace: {sourceBreakdown.marketplace || 0} · 
                External: {sourceBreakdown.external || 0} · 
                Government: {sourceBreakdown.government || 0} · 
                NCPB: {sourceBreakdown.ncpb || 0} {isSyntheticNcpb && <Chip label="Live Scrape" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />} · 
                News: {sourceBreakdown.news || 0} · 
                Mock: {sourceBreakdown.mock || 0}
              </Typography>
              {isSyntheticNcpb && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                  * Showing live scraped data from NCPB portal as no historical records found in database.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Planning Recommendations
            </Typography>
            <ul>
              <li>Optimal planting time based on rainfall patterns: {selectedCrop.cropName === 'Maize' ? 'March-April (long rains) and October-November (short rains)' : 'Varies by crop'}</li>
              <li>Consider selling {selectedCrop.cropName} during {selectedCrop.marketTrend.reduce((max, current, idx, arr) => current.price > arr[max].price ? idx : max, 0) === 0 ? 'January' : selectedCrop.marketTrend.reduce((max, current, idx, arr) => current.price > arr[max].price ? idx : max, 0) + 1} when prices tend to be highest</li>
              <li>Expected yield per hectare: {selectedCrop.cropName === 'Maize' ? '1.5-3.5 tons' : selectedCrop.cropName === 'Coffee' ? '0.5-1.5 tons' : selectedCrop.cropName === 'Tea' ? '8-10 tons of green leaf' : selectedCrop.cropName === 'Rice' ? '3.5-5 tons' : 'Varies by crop'}</li>
            </ul>
          </CardContent>
        </Card>
      );
    } catch (error) {
      console.error("Error generating market analysis:", error);
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Market Analysis
            </Typography>
            <Alert severity="error">
              Failed to generate market analysis. Please try again.
            </Alert>
          </CardContent>
        </Card>
      );
    }
  };

  // Render fallback content if charts have errors
  if (chartsError) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
            Agricultural Planning Dashboard
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            There was an error rendering the charts. This may be due to browser compatibility issues.
          </Alert>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Reload Page
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Agricultural Planning Dashboard
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Comprehensive information to help farmers make informed decisions
        </Typography>

        {/* Search Section */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                value={countyQuery}
                onChange={handleCountyChange}
                options={availableCounties}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select County"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                value={cropQuery}
                onChange={handleCropChange}
                options={availableCrops}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Crop"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{ minWidth: 120 }}>
                <InputLabel id="price-source-label">Price Source</InputLabel>
                <Select
                  labelId="price-source-label"
                  value={priceSource}
                  label="Price Source"
                  onChange={handlePriceSourceChange}
                >
                  <MenuItem value="official">Official stats</MenuItem>
                  <MenuItem value="marketplace">Marketplace trades</MenuItem>
                  <MenuItem value="ncpb">NCPB reference</MenuItem>
                  <MenuItem value="blended">Blended view</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Update'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleExport}
                disabled={loading || !selectedCounty || !selectedCrop}
                startIcon={<Download />}
                sx={{ height: '56px' }}
              >
                Export
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Analysis Section */}
            {selectedCounty && selectedCrop && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  {generateSuitabilityAnalysis()}
                </Grid>
                <Grid item xs={12} md={6}>
                  {generateMarketAnalysis()}
                </Grid>
              </Grid>
            )}

            {/* County Information */}
            {selectedCounty && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom color="primary">
                  {selectedCounty.name} County Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Climate & Geographic Data
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>Region:</strong> {selectedCounty.region}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Temperature:</strong> {selectedCounty.climate.temperature}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Rainfall:</strong> {selectedCounty.climate.rainfall}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Altitude:</strong> {selectedCounty.climate.altitude}
                          </Typography>
                        </Box>

                        <Typography variant="h6" gutterBottom>
                          Main Crops
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedCounty.mainCrops.map((crop, index) => (
                            <Chip 
                              key={index} 
                              label={crop} 
                              color={crop === selectedCrop?.cropName ? "primary" : "default"} 
                              variant={crop === selectedCrop?.cropName ? "filled" : "outlined"} 
                              onClick={() => {
                                setCropQuery(crop);
                                runSearch(undefined, crop);
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Market Prices in {selectedCounty.name}
                        </Typography>
                        {renderMarketPriceChart()}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Farming Tips for {selectedCounty.name} County
                        </Typography>
                        <Grid container spacing={2}>
                          {selectedCounty.farmingTips.map((tipCategory, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                  {tipCategory.category}
                                </Typography>
                                <ul>
                                  {tipCategory.tips.map((tip, tipIndex) => (
                                    <li key={tipIndex}>{tip}</li>
                                  ))}
                                </ul>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Crop Information */}
            {selectedCrop && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom color="primary">
                  {selectedCrop.cropName} Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {selectedCrop.cropName} Performance by County
                        </Typography>
                        {renderCropComparisonChart()}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>National Average:</strong> {selectedCrop.nationalAverage}/100
                          </Typography>
                          {selectedCounty && selectedCrop.countyData[selectedCounty.name] && (
                            <Typography variant="body2" gutterBottom>
                              <strong>{selectedCounty.name} Score:</strong> {selectedCrop.countyData[selectedCounty.name]}/100
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {selectedCrop.cropName} Market Price Trend (Annual)
                        </Typography>
                        {renderMarketTrendChart()}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Growing Conditions for {selectedCrop.cropName}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {Object.entries(selectedCrop.growingConditions).map(([condition, value], index) => (
                            <Typography key={index} variant="body2" gutterBottom>
                              <strong>{condition.charAt(0).toUpperCase() + condition.slice(1)}:</strong> {value}
                            </Typography>
                          ))}
                        </Box>

                        <Typography variant="h6" gutterBottom>
                          Best Practices
                        </Typography>
                        <ul>
                          {selectedCrop.bestPractices.map((practice, index) => (
                            <li key={index}>{practice}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          International Comparison
                        </Typography>
                        {renderInternationalComparisonChart()}
                        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                          Data Source: {selectedCrop.dataSource}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Data Sources & Citations */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Data Source: {dataSource}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This dashboard integrates official agronomic and economic statistics via the backend API,
                using publicly available datasets from institutions such as:
              </Typography>
              <ul>
                <li>
                  Kenya National Bureau of Statistics (KNBS) – Agriculture and Food Security Statistics
                </li>
                <li>
                  National Cereals and Produce Board (NCPB) – Strategic grain reserve and reference market prices
                </li>
                <li>
                  Food and Agriculture Organization of the United Nations (FAO) – FAOSTAT and GIEWS databases
                </li>
                <li>
                  World Bank Open Data – Agriculture & Rural Development Indicators
                </li>
                <li>
                  Kenya Ministry of Agriculture and Livestock Development (MoALD) publications and bulletins
                </li>
              </ul>
              {sourceCitations.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Additional source notes:
                  </Typography>
                  <ul>
                    {Array.from(new Set(sourceCitations)).map((citation, index) => (
                      <li key={index}>{citation}</li>
                    ))}
                  </ul>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default AgriDashboard; 
